import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import { useQueryGetExamSet } from "@/apis/exam_sets/getExamSet";
import { useUpdateExamSet } from "@/apis/exam_sets/updateExamSet";
import ExamSetForm from "./components/ExamSetForm";

const ExamSetEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: examSet, isLoading, error } = useQueryGetExamSet(id || "");
  const { mutate: updateExamSet, isPending } = useUpdateExamSet();
  const [serverError, setServerError] = useState<string | undefined>();

  // Cancel handler
  const handleCancel = () => {
    navigate(`/exam_sets/${id}`);
  };

  // Submit handler
  const handleSubmit = (formData: any) => {
    if (!id) return;
    updateExamSet(
      { id, data: formData },
      {
        onSuccess: () => {
          navigate(`/exam_sets/${id}`);
        },
        onError: (error: any) => {
          setServerError(error?.message || "Failed to update exam set");
        },
      }
    );
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading exam set information...</Text>
        </div>
      </PageContainer>
    );
  }

  if (error || !examSet) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load exam set information. Please try again.</Text>
          <Button onClick={() => navigate("/exam_sets")} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="flex items-center gap-4 mb-4">
          <Button plain onClick={handleCancel}>
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </Button>
          <Heading>Edit Exam Set</Heading>
        </div>
        <Text className="text-zinc-500 mb-2">
          Update the information below to edit this exam set.
        </Text>
        <ExamSetForm
          initialData={examSet}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isPending}
          serverError={serverError}
          isEditMode={true}
        />
      </div>
    </PageContainer>
  );
};

export default ExamSetEditPage;
